<template>
	<v-card>
		<v-card-title
			class="d-flex align-center"
		>
			<v-icon
				icon="$insurance"
				start
			/>
			<translate>Insurance</translate>
		</v-card-title>
		<v-card-text>
			<v-row>
				<v-col>
					<v-switch
						v-if="multipleProductVersions"
						v-model="showAllActiveProducts"
						color="primary"
						:label="$gettext('Show old product versions')"
					/>
				</v-col>
			</v-row>
			<v-row>
				<v-col
					v-for="product in showInsurances"
					:key="product.id"
					cols="6"
				>
					<v-card>
						<v-card-title class="d-flex align-center">
							<v-img
								class="ma-1"
								max-height="24"
								max-width="24"
								location="start"
								src="/frontoffice/images/brand/icons/icon-228.png"
							/>
							<strong>{{ product.name }}</strong>
						</v-card-title>
						<v-card-subtitle>
							{{ product.domain_name + ' - ' + product.id }}
						</v-card-subtitle>
						<v-card-text>
							<v-row>
								<v-col>
									<v-list
										density="compact"
									>
										<v-list-item
											v-for="(item, key) in product.coverages"
											:key="key"
											:title="$gettext(item)"
											prepend-icon="$check"
										/>
										<v-list-item>
											<v-list-item-title><translate>Valid From</translate>: {{ product.valid_from }}</v-list-item-title>
										</v-list-item>
										<v-list-item>
											<v-list-item-title><translate>Valid To</translate>: {{ product.valid_until }}</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<div
										class="mr-2 pa-0 text-primary"
									>
										<translate>Monthly terminable</translate>
									</div>
								</v-col>
							</v-row>
							<v-row v-if="hasPrice(product.id)">
								<v-col>
									<v-row>
										<v-col>
											<translate>First 3 months for</translate> <strong><translate>free</translate></strong>, <translate>thereafter</translate>
										</v-col>
									</v-row>
									<v-row no-gutters>
										<v-col>
											<span class="mr-2 text-h6 font-weight-bold text-secondary">€{{ prices[product.id] }}</span>
											<translate>Per month</translate>
										</v-col>
									</v-row>
								</v-col>
							</v-row>
							<v-row v-else>
								<v-col>
									<span><translate>Unavailable for the selected device</translate></span>
								</v-col>
							</v-row>
						</v-card-text>
						<v-card-actions class="card-actions">
							<v-spacer />
							<v-btn
								color="primary"
								variant="flat"
								:disabled="!hasPrice(product.id)"
								@click="$emit('update:productVersionId', product.id)"
							>
								<translate>Insure</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-col>
			</v-row>
		</v-card-text>
		<v-card-actions>
			<v-btn
				variant="plain"
				@click="$emit('update:previous')"
			>
				<translate>Back</translate>
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import axios from 'axios';
import {
	displayError,
} from 'laravel-front-library';

export default {
	name: 'OrderInsuranceSelect',
	props: {
		insuranceStartDate: {
			type: String,
			required: true,
		},
		deviceId: {
			type: Number,
			required: true,
		},
		domainId: {
			type: Number,
			required: true,
		}
	},

	emits: ['update:productVersionId', 'update:previous',],

	data () {
		return {
			prices: null,
			insurances: [],
			showAllActiveProducts: false,
		};
	},

	computed: {
		/**
		 * Returns insurances filtered on active
		 *
		 * @returns {Array} array of active insurances
		 */
		showInsurances: function () {
			return this.insurances.filter((insurance) => {
				const isFromDomain = this.domainId === insurance.domain_id;

				return (insurance.is_current || this.showAllActiveProducts) && isFromDomain;
			}).sort((productA, productB) => {
				return productA.id > productB.id ? 1 : -1; // show Mobile Essential first
			});
		},

		/**
		 * Check if there is a product with multiple product versions retrieved
		 *
		 * @returns {boolean|null} If we have a insurance with multiple versions active
		 */
		multipleProductVersions: function () {
			if(this.insurances) {
				return this.insurances.some((insurance) => insurance.is_current === false);
			}
			return null;
		},
	},

	watch: {
		deviceId () {
			this.getUpdatedPrice();
		},
	},


	/**
	 * Get all the necessary data from the api.
	 *
	 * @returns {void}
	 */
	created () {
		this.getInsurances();
		this.getUpdatedPrice();
	},

	methods: {

		/**
		 * Check if the product has a price attached to it for the specific ean
		 *
		 * @param {number} id - id of the product
		 * @returns {boolean} whether the product has a price attached to it
		 */
		hasPrice (id) {
			if(this.prices) {
				return id in this.prices;
			}
			return false;
		},

		/**
		 * Get products/insurances
		 *
		 * @returns {void}
		 */
		getInsurances () {
			axios.get('add/currentproducts', {
				params: {
					'start_date': this.insuranceStartDate
				}
			})
				.then(({
					data
				}) => {
					this.insurances = data;
				})
				.catch(() => {
					displayError(this.$gettext('Something has gone wrong during insurance fetch'));
				});
		},

		/**
		 * Get price again  if the selected device changes
		 *
		 * @returns {void}
		 */
		getUpdatedPrice () {
			axios.get('/api/v0/prices', {
				params: {
					device: this.deviceId,
					startDate: this.insuranceStartDate
				}
			})
				.then(({
					data
				}) => {
					if (data.success) {
						this.prices = data.prices;
					}
				}).catch(() => {
					displayError(this.$gettext('Something has gone wrong during price fetch'));
				});
		},
	}
};
</script>
